import type { components } from '@/types/api/v1'
import { cmp, orderBy } from '@/utils/orderBy'
import type { Dayjs } from 'dayjs'
import { useMemo } from 'react'
import type { ConflictError } from '../../utils/ConflictValidators'
import { getDriverShiftStart } from '../../utils/getDefaultShift'
import { useSchedulePageFilters } from '../Filters/SchedulePageFilterContext'
import type { ScheduleItemWithConflicts } from './scheduleItemStore'

export type RowItem = {
  id: string
  depot?: string | null
  name?: string | null
  shift_preference?: string | null
  am_shift?: components['schemas']['ShiftInfo']
  pm_shift?: components['schemas']['ShiftInfo']
  carrier_id?: string | null
  carrier_resource_num?: number | null
}

const isConflictError = <T>(error: ConflictError<T>) =>
  error.type === 'UniqueDriver' || error.type === 'UniqueTrailer' || error.type === 'UniqueTractor'
const isOosError = <T>(error: ConflictError<T>) =>
  error.type === 'OutOfServiceTractor' || error.type === 'InactiveTractor'
const isMissingEquipment = (item: ScheduleItemWithConflicts, filters: { startDate: Dayjs; endDate: Dayjs }) => {
  if (item.shift !== 'am' && item.shift !== 'pm') return false
  if (!!item.tractor_id && !!item.trailer_id && !!item.tractor_number && !!item.trailer_number) return false
  if (item.start >= filters.endDate || item.end <= filters.startDate) return false
  return true
}

export const useFilteredItems = <T extends RowItem>(rows: T[], items: ScheduleItemWithConflicts[]) => {
  const { depot, sortType, sortDirection, nameFilter, shiftFilter, viewMode, errorFilter, dateRange } =
    useSchedulePageFilters()
  return useMemo(() => {
    const errorSets = items.reduce((result, item) => {
      if (!errorFilter?.length) return result
      const key = viewMode === 'driver' ? item.driver_or_carrier_id : item.tractor_id
      if (!key) return result
      let current = result.get(key)
      if (current) return result
      current = errorFilter.reduce(
        (hasErrors, filter) =>
          hasErrors ||
          (filter === 'conflict' && item.errors.some(isConflictError)) ||
          (filter === 'oos' && item.errors.some(isOosError)) ||
          (filter === 'missing' && isMissingEquipment(item, dateRange)),
        false
      )
      result.set(key, current)
      return result
    }, new Map<string, boolean>())
    const rowIdSet = new Set()
    const filteredRows = rows
      ?.filter(
        (x) =>
          (!depot?.length || depot.some((y) => x.depot?.includes(y))) &&
          (!nameFilter || x.name?.toLocaleLowerCase()?.includes(nameFilter.toLocaleLowerCase())) &&
          (!shiftFilter || x.shift_preference === shiftFilter) &&
          (!errorFilter?.length || errorSets.get(x.id))
      )
      ?.map((x) => {
        rowIdSet.add(x.id)
        return {
          ...x,
          start: getDriverShiftStart({ ...x, status: '', template_equipment_overrides: [] })?.valueOf() ?? 0,
        }
      })

    let orderQuery = orderBy<(typeof filteredRows)[0]>((x) => !!x.carrier_id)

    orderQuery =
      sortType === 'shiftStart'
        ? orderQuery.thenBy((x) => x.start, cmp.number, sortDirection)
        : orderQuery
            .thenBy((x) => (sortType === 'depot' || x.carrier_id ? x.depot : x.name) ?? '', cmp.string, sortDirection)
            .thenBy((x) => x.carrier_resource_num ?? 0, cmp.number, sortDirection)

    const finalRows = filteredRows.sort(orderQuery)
    const filteredItems =
      viewMode === 'tractor'
        ? items.filter((x) => x.tractor_id && rowIdSet.has(x.tractor_id))
        : items.filter((x) => x.driver_or_carrier_id && rowIdSet.has(x.driver_or_carrier_id))
    return {
      filteredRows: finalRows,
      filteredItems,
    }
  }, [depot, nameFilter, sortDirection, sortType, shiftFilter, rows, items, viewMode, errorFilter, dateRange])
}
